import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Camera, User, MapPin, AtSign, Calendar, Users, Save, X, Upload, Video, Play, Trash2, Heart, Volume2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { api } from '../services/api';
import { toast } from 'sonner';
import soundSystem from '../utils/sounds';
import SoundSelector from './SoundSelector';

export default function ProfileEditor({ customer, onUpdate, onClose }) {
  const [isLoading, setIsLoading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);

  const [profile, setProfile] = useState({
    firstName: customer?.firstName || '',
    lastName: customer?.lastName || '',
    email: customer?.email || '',
    birthDate: customer?.birthDate || '',
    address: customer?.address || '',
    postalCode: customer?.postalCode || '',
    city: customer?.city || '',
    // Réseaux sociaux
    facebook: customer?.facebook || '',
    whatsapp: customer?.whatsapp || '',
    instagram: customer?.instagram || '',
    messenger: customer?.messenger || '',
    snapchat: customer?.snapchat || '',
    tiktok: customer?.tiktok || '',
    twitter: customer?.twitter || '',
    pinterest: customer?.pinterest || '',
    linkedin: customer?.linkedin || '',
    vk: customer?.vk || '',
    profilePhoto: customer?.profilePhoto || '',
    guardianFirstName: customer?.guardianFirstName || '',
    guardianLastName: customer?.guardianLastName || '',
    guardianPhone: customer?.guardianPhone || ''
  });

  const [isMinor, setIsMinor] = useState(customer?.isMinor || false);

  // 📹 États pour la vidéo de profil
  const [profileVideo, setProfileVideo] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [showVideoRecorder, setShowVideoRecorder] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const videoPreviewRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const videoFileInputRef = useRef(null);
  const recordingIntervalRef = useRef(null);

  // Charger la vidéo existante
  useEffect(() => {
    const loadVideo = async () => {
      if (customer?.id) {
        try {
          const res = await api.getProfileVideos(customer.id);
          if (res.videos && res.videos.length > 0) {
            setProfileVideo(res.videos[0]);
          }
        } catch (error) {
          console.log('Pas de vidéo existante');
        }
      }
    };
    loadVideo();
  }, [customer?.id]);

  // Calculate age when birthDate changes
  useEffect(() => {
    if (profile.birthDate) {
      const birth = new Date(profile.birthDate);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      setIsMinor(age < 18);
    } else {
      setIsMinor(false);
    }
  }, [profile.birthDate]);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const startCamera = async () => {
    try {
      // Configuration optimisée pour mobile (selfie)
      const constraints = {
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 640 }
        },
        audio: false
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Attendre que la vidéo soit prête
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().catch(err => {
            console.error('Erreur lecture vidéo:', err);
          });
        };
      }
      setShowCamera(true);
      toast.success('Caméra activée !');
    } catch (err) {
      console.error('Erreur caméra:', err);
      if (err.name === 'NotAllowedError') {
        toast.error("Veuillez autoriser l'accès à la caméra dans les paramètres");
      } else if (err.name === 'NotFoundError') {
        toast.error("Aucune caméra trouvée sur cet appareil");
      } else if (err.name === 'NotReadableError') {
        toast.error("La caméra est utilisée par une autre application");
      } else {
        toast.error("Impossible d'accéder à la caméra. Essayez de rafraîchir la page.");
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  const takePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      
      // Vérifier que la vidéo est prête
      if (video.readyState !== video.HAVE_ENOUGH_DATA) {
        toast.error('La caméra n\'est pas encore prête, réessayez');
        return;
      }
      
      // Vérifier les dimensions de la vidéo
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        toast.error('La vidéo n\'est pas encore chargée, patientez');
        return;
      }
      
      canvas.width = 300;
      canvas.height = 300;
      const ctx = canvas.getContext('2d');
      
      // Center crop
      const size = Math.min(video.videoWidth, video.videoHeight);
      const x = (video.videoWidth - size) / 2;
      const y = (video.videoHeight - size) / 2;
      
      // Appliquer l'effet miroir pour selfie
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      
      ctx.drawImage(video, x, y, size, size, 0, 0, 300, 300);
      
      // Réinitialiser la transformation
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      
      const photoData = canvas.toDataURL('image/jpeg', 0.8);
      
      if (photoData && photoData.length > 100) {
        setProfile(prev => ({ ...prev, profilePhoto: photoData }));
        stopCamera();
        toast.success('📸 Photo prise avec succès !');
      } else {
        toast.error('📷 Capture échouée. Réessayez en regardant la caméra.');
      }
    } else {
      toast.error('📷 Caméra non disponible. Essayez d\'importer une photo.');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('📁 Photo trop lourde (max 5 Mo). Choisissez une autre photo.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        // Resize image
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = 300;
          canvas.height = 300;
          const ctx = canvas.getContext('2d');
          
          // Center crop
          const size = Math.min(img.width, img.height);
          const x = (img.width - size) / 2;
          const y = (img.height - size) / 2;
          
          ctx.drawImage(img, x, y, size, size, 0, 0, 300, 300);
          const resizedPhoto = canvas.toDataURL('image/jpeg', 0.7);
          setProfile(prev => ({ ...prev, profilePhoto: resizedPhoto }));
          toast.success('📸 Photo ajoutée !');
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  // 📹 FONCTIONS VIDÉO
  const startVideoRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' }, 
        audio: true 
      });
      
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
      }
      
      streamRef.current = stream;
      recordedChunksRef.current = [];
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          recordedChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        processRecordedVideo(blob);
      };
      
      setShowVideoRecorder(true);
      toast.success('🎬 Caméra prête ! Appuie sur Enregistrer');
    } catch (error) {
      toast.error('📹 Impossible d\'accéder à la caméra');
    }
  };

  const startRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 60) {
            stopRecording();
            return 60;
          }
          return prev + 1;
        });
      }, 1000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };

  const cancelVideoRecording = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }
    setShowVideoRecorder(false);
    setIsRecording(false);
    setRecordingTime(0);
  };

  const processRecordedVideo = async (blob) => {
    setVideoLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        
        const res = await api.uploadProfileVideo(
          customer.id, 
          base64, 
          recordingTime,
          'Ma vidéo de présentation'
        );
        
        if (res.success) {
          // Recharger la vidéo
          const videosRes = await api.getProfileVideos(customer.id);
          if (videosRes.videos && videosRes.videos.length > 0) {
            setProfileVideo(videosRes.videos[0]);
          }
          toast.success('📹 Vidéo uploadée avec succès !');
        }
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      toast.error('❌ Erreur lors de l\'upload');
    } finally {
      setVideoLoading(false);
      setShowVideoRecorder(false);
    }
  };

  const handleVideoFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 50 * 1024 * 1024) {
      toast.error('📁 Vidéo trop lourde (max 50 Mo)');
      return;
    }
    
    // Vérifier la durée
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = async () => {
      if (video.duration > 60) {
        toast.error('⏱️ Vidéo trop longue (max 60 secondes)');
        return;
      }
      
      setVideoLoading(true);
      try {
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = reader.result.split(',')[1];
          
          const res = await api.uploadProfileVideo(
            customer.id, 
            base64, 
            Math.round(video.duration),
            'Ma vidéo de présentation'
          );
          
          if (res.success) {
            const videosRes = await api.getProfileVideos(customer.id);
            if (videosRes.videos && videosRes.videos.length > 0) {
              setProfileVideo(videosRes.videos[0]);
            }
            toast.success('📹 Vidéo uploadée !');
          }
        };
        reader.readAsDataURL(file);
      } catch (error) {
        toast.error('❌ Erreur lors de l\'upload');
      } finally {
        setVideoLoading(false);
      }
    };
    video.src = URL.createObjectURL(file);
  };

  const deleteVideo = async () => {
    if (!profileVideo) return;
    
    try {
      await api.deleteProfileVideo(profileVideo.id, customer.id);
      setProfileVideo(null);
      toast.success('🗑️ Vidéo supprimée');
    } catch (error) {
      toast.error('❌ Erreur lors de la suppression');
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const updatedCustomer = await api.updateCustomerProfile(customer.id, profile);
      toast.success('✅ Profil mis à jour !');
      onUpdate(updatedCustomer);
      onClose();
    } catch (error) {
      toast.error('❌ Impossible de sauvegarder. Réessayez.');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 overflow-y-auto"
    >
      <div className="min-h-screen py-8 px-4">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto backdrop-blur-xl rounded-3xl p-6 border-2"
          style={{
            background: 'rgba(10, 0, 30, 0.95)',
            borderColor: '#FF00FF',
            boxShadow: '0 0 40px rgba(255, 0, 255, 0.4)'
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <User className="w-7 h-7 text-pink-400" />
              Mon Profil
            </h2>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-white cursor-pointer p-2 rounded-full hover:bg-white/10 transition-all z-50"
              data-testid="btn-close-profile"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Photo Section */}
          <div className="mb-8 text-center">
            <div className="relative inline-block">
              {showCamera ? (
                <div className="relative">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-48 h-48 rounded-full object-cover border-4 border-pink-500"
                    style={{ transform: 'scaleX(-1)' }}
                  />
                  <canvas ref={canvasRef} className="hidden" />
                  <div className="flex gap-2 mt-3 justify-center">
                    <Button onClick={takePhoto} className="bg-green-500 hover:bg-green-600">
                      <Camera className="w-4 h-4 mr-1" /> Capturer
                    </Button>
                    <Button onClick={stopCamera} variant="outline" className="border-gray-500">
                      Annuler
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {profile.profilePhoto ? (
                    <img
                      src={profile.profilePhoto}
                      alt="Photo de profil"
                      className="w-48 h-48 rounded-full object-cover border-4 border-pink-500"
                    />
                  ) : (
                    <div className="w-48 h-48 rounded-full bg-gray-800 border-4 border-pink-500 flex items-center justify-center">
                      <User className="w-20 h-20 text-gray-500" />
                    </div>
                  )}
                  <div className="flex gap-2 mt-3 justify-center">
                    <Button onClick={startCamera} size="sm" className="bg-pink-500 hover:bg-pink-600">
                      <Camera className="w-4 h-4 mr-1" /> Photo
                    </Button>
                    <Button 
                      onClick={() => fileInputRef.current?.click()} 
                      size="sm" 
                      variant="outline"
                      className="border-pink-500 text-pink-400"
                    >
                      <Upload className="w-4 h-4 mr-1" /> Galerie
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Form */}
          <div className="space-y-6">
            {/* Identity */}
            <div className="p-4 rounded-xl bg-black/30 border border-cyan-500/30">
              <h3 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                <User className="w-5 h-5" /> Identité
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Prénom</Label>
                  <Input
                    value={profile.firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    placeholder="Votre prénom"
                    className="bg-black/50 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Nom</Label>
                  <Input
                    value={profile.lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    placeholder="Votre nom"
                    className="bg-black/50 border-gray-600 text-white mt-1"
                  />
                </div>
              </div>
              <div className="mt-4">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Date de naissance
                </Label>
                <Input
                  type="date"
                  value={profile.birthDate}
                  onChange={(e) => handleChange('birthDate', e.target.value)}
                  className="bg-black/50 border-gray-600 text-white mt-1"
                />
                {isMinor && (
                  <p className="text-yellow-400 text-sm mt-2">
                    ⚠️ Vous êtes mineur(e). L&apos;autorisation parentale est requise pour certains produits.
                  </p>
                )}
              </div>
            </div>

            {/* Guardian (if minor) */}
            {isMinor && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/50"
              >
                <h3 className="text-lg font-semibold text-yellow-400 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5" /> Responsable Légal
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Prénom du responsable</Label>
                    <Input
                      value={profile.guardianFirstName}
                      onChange={(e) => handleChange('guardianFirstName', e.target.value)}
                      placeholder="Prénom"
                      className="bg-black/50 border-yellow-500/50 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Nom du responsable</Label>
                    <Input
                      value={profile.guardianLastName}
                      onChange={(e) => handleChange('guardianLastName', e.target.value)}
                      placeholder="Nom"
                      className="bg-black/50 border-yellow-500/50 text-white mt-1"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <Label className="text-gray-300">Téléphone du responsable</Label>
                  <Input
                    value={profile.guardianPhone}
                    onChange={(e) => handleChange('guardianPhone', e.target.value)}
                    placeholder="06 XX XX XX XX"
                    className="bg-black/50 border-yellow-500/50 text-white mt-1"
                  />
                </div>
              </motion.div>
            )}

            {/* Contact */}
            <div className="p-4 rounded-xl bg-black/30 border border-green-500/30">
              <h3 className="text-lg font-semibold text-green-400 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5" /> Coordonnées
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300">Email</Label>
                  <Input
                    type="email"
                    value={profile.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="votre@gmail.com"
                    className="bg-black/50 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Adresse</Label>
                  <Input
                    value={profile.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    placeholder="123 rue Example"
                    className="bg-black/50 border-gray-600 text-white mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Code postal</Label>
                    <Input
                      value={profile.postalCode}
                      onChange={(e) => handleChange('postalCode', e.target.value)}
                      placeholder="75000"
                      className="bg-black/50 border-gray-600 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Ville</Label>
                    <Input
                      value={profile.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                      placeholder="Paris"
                      className="bg-black/50 border-gray-600 text-white mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 🔊 Son de Bienvenue Préféré */}
            <div className="p-4 rounded-xl bg-black/30 border border-cyan-500/30">
              <h3 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                <Volume2 className="w-5 h-5" /> Mon Son de Bienvenue 🎮
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                Choisis le son qui t&apos;accueille à chaque connexion ! 
                <span className="text-cyan-300"> (ou laisse &quot;Surprise&quot; pour un son aléatoire)</span>
              </p>
              <SoundSelector />
            </div>

            {/* Social Media */}
            <div className="p-4 rounded-xl bg-black/30 border border-purple-500/30">
              <h3 className="text-lg font-semibold text-purple-400 mb-4 flex items-center gap-2">
                <AtSign className="w-5 h-5" /> Réseaux Sociaux
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {/* Top 3 - Les plus utilisés */}
                <div>
                  <Label className="text-gray-300">📘 Facebook</Label>
                  <Input
                    value={profile.facebook}
                    onChange={(e) => handleChange('facebook', e.target.value)}
                    placeholder="Nom ou lien"
                    className="bg-black/50 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">💬 WhatsApp</Label>
                  <Input
                    value={profile.whatsapp}
                    onChange={(e) => handleChange('whatsapp', e.target.value)}
                    placeholder="Numéro ou lien"
                    className="bg-black/50 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">📸 Instagram</Label>
                  <Input
                    value={profile.instagram}
                    onChange={(e) => handleChange('instagram', e.target.value)}
                    placeholder="@pseudo_insta"
                    className="bg-black/50 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">💭 Messenger</Label>
                  <Input
                    value={profile.messenger}
                    onChange={(e) => handleChange('messenger', e.target.value)}
                    placeholder="Nom ou lien"
                    className="bg-black/50 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">👻 Snapchat</Label>
                  <Input
                    value={profile.snapchat}
                    onChange={(e) => handleChange('snapchat', e.target.value)}
                    placeholder="pseudo_snap"
                    className="bg-black/50 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">🎵 TikTok</Label>
                  <Input
                    value={profile.tiktok}
                    onChange={(e) => handleChange('tiktok', e.target.value)}
                    placeholder="@pseudo_tiktok"
                    className="bg-black/50 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">🐦 Twitter (X)</Label>
                  <Input
                    value={profile.twitter}
                    onChange={(e) => handleChange('twitter', e.target.value)}
                    placeholder="@pseudo_x"
                    className="bg-black/50 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">📌 Pinterest</Label>
                  <Input
                    value={profile.pinterest}
                    onChange={(e) => handleChange('pinterest', e.target.value)}
                    placeholder="Pseudo ou lien"
                    className="bg-black/50 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">💼 LinkedIn</Label>
                  <Input
                    value={profile.linkedin}
                    onChange={(e) => handleChange('linkedin', e.target.value)}
                    placeholder="Profil ou lien"
                    className="bg-black/50 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">🔵 VK</Label>
                  <Input
                    value={profile.vk}
                    onChange={(e) => handleChange('vk', e.target.value)}
                    placeholder="Pseudo ou lien"
                    className="bg-black/50 border-gray-600 text-white mt-1"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="mt-8 flex gap-3">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1 border-gray-500 text-gray-300"
            >
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              disabled={isLoading}
              className="flex-1 font-bold"
              style={{
                background: 'linear-gradient(135deg, #FF00FF 0%, #00FFFF 100%)',
                color: '#000'
              }}
            >
              <Save className="w-5 h-5 mr-2" />
              {isLoading ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
